export type BuiltInNavSection = 'conversations' | 'direct-messages' | 'channels' | 'external' | 'spam' | 'drafts-sent';
export type NavSection = BuiltInNavSection | `custom:${string}`;
export type DraftsSentTab = 'scheduled' | 'sent';
export type FilterStatus = 'all' | 'signed' | 'unsigned';
export type FilterAiMuted = 'all' | 'muted' | 'unmuted';
export type FilterChannel = 'all' | 'whatsApp' | 'telegram' | 'instagram' | 'messenger' | 'webchat';
export type FilterWhatsAppThread = 'all' | 'direct' | 'groups';

export const CUSTOM_LIST_NAV_PREFIX = 'custom:' as const;

export const isCustomListNavSection = (navSection: NavSection): navSection is `custom:${string}` =>
  navSection.startsWith(CUSTOM_LIST_NAV_PREFIX);

export const getCustomListIdFromNavSection = (navSection: NavSection): string | null =>
  isCustomListNavSection(navSection) ? navSection.slice(CUSTOM_LIST_NAV_PREFIX.length) : null;

/** Maps inbox nav tabs to API ConversationKind filter (server-side pagination). */
export const getConversationKindForNavSection = (
  navSection: NavSection,
): 'direct' | 'channel' | 'external' | undefined => {
  if (isCustomListNavSection(navSection) || navSection === 'spam' || navSection === 'drafts-sent') {
    return undefined;
  }

  switch (navSection) {
    case 'direct-messages':
      return 'direct';
    case 'channels':
      return 'channel';
    case 'external':
      return 'external';
    default:
      return undefined;
  }
};

export const buildCustomListNavSection = (listId: string): NavSection => `${CUSTOM_LIST_NAV_PREFIX}${listId}`;

/** URL ?view= value → NavSection */
export const VIEW_PARAM_TO_SECTION: Readonly<Record<string, BuiltInNavSection>> = {
  direct: 'direct-messages',
  channels: 'channels',
  external: 'external',
  spam: 'spam',
  hidden: 'spam',
  all: 'conversations',
};

/** NavSection → URL ?view= value (only built-in sections that need one) */
export const SECTION_TO_VIEW_PARAM: Partial<Readonly<Record<BuiltInNavSection, string>>> = {
  'direct-messages': 'direct',
  channels: 'channels',
  external: 'external',
  spam: 'spam',
};

export const parseNavSectionFromSearchParams = (params: URLSearchParams): NavSection => {
  const viewParam = params.get('view') ?? '';
  if (viewParam === 'custom') {
    const listId = params.get('list')?.trim();
    if (listId) {
      return buildCustomListNavSection(listId);
    }
  }

  return VIEW_PARAM_TO_SECTION[viewParam] ?? 'conversations';
};

export const applyNavSectionToSearchParams = (params: URLSearchParams, navSection: NavSection): void => {
  const viewParam = SECTION_TO_VIEW_PARAM[navSection as BuiltInNavSection];
  if (viewParam) {
    params.set('view', viewParam);
    params.delete('list');
    return;
  }

  if (isCustomListNavSection(navSection)) {
    params.set('view', 'custom');
    params.set('list', getCustomListIdFromNavSection(navSection)!);
    return;
  }

  params.delete('view');
  params.delete('list');
};
